import React, { useState, useRef, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import {
  Bars3Icon,
  XMarkIcon,
  ChartBarIcon,
  UserIcon,
  BellIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { RootState } from '../../store/store'
import { logout } from '../../store/slices/authSlice'
import { subscriptionsApi } from '../../services/api'
import Button from './Button'
import logoIcon from '../../assets/logo-icon.png'

interface NavigationProps {
  isPrivate?: boolean
  isAdmin?: boolean
}

const Navigation: React.FC<NavigationProps> = ({ isPrivate = false, isAdmin = false }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  // null = unknown (still loading) — we only lock once we know it's false, to avoid a lock flash
  const [hasActivePlan, setHasActivePlan] = useState<boolean | null>(null)
  const userMenuRef = useRef<HTMLDivElement>(null)

  const handleLogout = () => {
    dispatch(logout() as any)
    setUserMenuOpen(false)
  }

  // Fetch plan status so we can lock plan-gated sections for users without an active plan
  useEffect(() => {
    if (!isPrivate || !isAuthenticated || user?.is_admin) return
    let mounted = true
    subscriptionsApi.getSubscriptionStatus()
      .then(res => { if (mounted) setHasActivePlan(res.has_active_plan) })
      .catch(() => { if (mounted) setHasActivePlan(false) })
    return () => { mounted = false }
  }, [isPrivate, isAuthenticated, user?.is_admin])

  // A locked item can't be opened — instead we nudge the user to buy a plan
  const handleLockedClick = () => {
    setMobileMenuOpen(false)
    toast('Subscribe to a plan to unlock this section')
    navigate('/dashboard/subscription')
  }

  // Close the user dropdown on Escape or click outside (keyboard accessibility)
  useEffect(() => {
    if (!userMenuOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setUserMenuOpen(false)
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [userMenuOpen])

  const publicNavItems = [
    { name: 'Home', href: '/', current: location.pathname === '/' },
    { name: 'Pricing', href: '/pricing', current: location.pathname === '/pricing' },
    { name: 'Blogs', href: '/blogs', current: location.pathname.startsWith('/blogs') || location.pathname === '/academy' },
    { name: 'About', href: '/about', current: location.pathname === '/about' },
    { name: 'Contact', href: '/contact', current: location.pathname === '/contact' },
  ]

  const privateNavItems = [
    { name: 'Dashboard', href: '/dashboard', current: location.pathname === '/dashboard', icon: ChartBarIcon, requiresPlan: false },
    { name: 'Recommendations', href: '/dashboard/recommendations', current: location.pathname === '/dashboard/recommendations', icon: ChartBarIcon, requiresPlan: true },
    { name: 'Profile', href: '/dashboard/profile', current: location.pathname === '/dashboard/profile', icon: UserIcon, requiresPlan: false },
    { name: 'Notifications', href: '/dashboard/notifications', current: location.pathname === '/dashboard/notifications', icon: BellIcon, requiresPlan: false },
    { name: 'Subscription', href: '/dashboard/subscription', current: location.pathname === '/dashboard/subscription', icon: Cog6ToothIcon, requiresPlan: false }
  ]

  const adminNavItems = [
    { name: 'Admin Dashboard', href: '/admin', current: location.pathname === '/admin', icon: ChartBarIcon },
    { name: 'User Management', href: '/admin/users', current: location.pathname === '/admin/users', icon: UserIcon },
    { name: 'Content Management', href: '/admin/content', current: location.pathname === '/admin/content', icon: Cog6ToothIcon }
  ]

  const navItems = isAdmin ? adminNavItems : isPrivate ? privateNavItems : publicNavItems

  // Locked once we positively know there's no active plan (null = still loading)
  const isLocked = (item: { name: string; href: string; current: boolean; requiresPlan?: boolean }) =>
    isPrivate && !user?.is_admin && Boolean(item.requiresPlan) && hasActivePlan === false

  return (
    <nav aria-label="Primary" className="sticky top-0 z-50 bg-dark-900/95 backdrop-blur-md border-b border-dark-700/50 shadow-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo Section */}
          <div className="flex items-center space-x-4">
            <Link to="/" aria-label="AnalyzerView home" className="flex items-center space-x-3 group">
              <div className="relative">
                <img
                  src={logoIcon}
                  alt=""
                  className="w-11 h-11 object-contain group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="flex flex-col">
                <h1 className="text-2xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent tracking-tight">
                  AnalyzerView
                </h1>
                <p className="text-xs text-gray-400 font-medium tracking-wide">RESEARCH PLATFORM</p>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex lg:items-center lg:space-x-1">
            {navItems.map((item) => (
              isLocked(item) ? (
                <button
                  key={item.name}
                  type="button"
                  onClick={handleLockedClick}
                  title="Subscribe to unlock"
                  aria-label={`${item.name} — locked, subscribe to unlock`}
                  className="group relative flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-500 hover:text-gray-300 hover:bg-dark-800/40 transition-all duration-300"
                >
                  <span className="relative z-10">{item.name}</span>
                  <LockClosedIcon aria-hidden="true" className="w-3.5 h-3.5" />
                </button>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  aria-current={item.current ? 'page' : undefined}
                  className={`group relative px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
                    item.current
                      ? 'text-primary-400 bg-primary-500/10 shadow-inner'
                      : 'text-gray-300 hover:text-white hover:bg-dark-800/60'
                  }`}
                >
                  <span className="relative z-10">
                    {item.name}
                  </span>
                  {item.current && (
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-6 h-0.5 bg-primary-400 rounded-full"></div>
                  )}
                  {!item.current && (
                    <div className="absolute inset-0 bg-gradient-to-r from-primary-500/0 via-primary-500/5 to-primary-500/0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  )}
                </Link>
              )
            ))}
          </div>

          {/* Right Side - Authentication */}
          <div className="hidden lg:flex lg:items-center lg:space-x-6">
            {isAuthenticated ? (
              <div className="flex items-center space-x-4">
                {/* User Profile Dropdown */}
                <div className="relative" ref={userMenuRef}>
                  <button
                    type="button"
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    aria-expanded={userMenuOpen}
                    aria-haspopup="menu"
                    aria-controls="user-menu"
                    className="flex items-center space-x-3 p-2 rounded-xl bg-dark-800/40 hover:bg-dark-800 transition-all duration-200 border border-dark-700/50 hover:border-dark-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-dark-900"
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-primary-400 to-primary-600 rounded-lg flex items-center justify-center text-white font-semibold text-sm">
                      {user?.full_name?.charAt(0) || 'U'}
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-white truncate max-w-24">
                        {user?.full_name?.split(' ')[0] || 'User'}
                      </p>
                      <p className="text-xs text-gray-400">
                        {hasActivePlan === false ? 'Free' : hasActivePlan ? 'Premium' : ''}
                      </p>
                    </div>
                    <ChevronDownIcon aria-hidden="true" className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* User Dropdown Menu */}
                  {userMenuOpen && (
                    <div id="user-menu" role="menu" aria-label="User menu" className="absolute right-0 mt-2 w-64 bg-dark-800 rounded-xl shadow-2xl border border-dark-700 py-2 z-50">
                      <div className="px-4 py-3 border-b border-dark-700">
                        <p className="text-white font-medium">{user?.full_name || 'User Name'}</p>
                        <p className="text-sm text-gray-400">{user?.email || 'user@email.com'}</p>
                        {hasActivePlan === false ? (
                          <Link
                            to="/dashboard/subscription"
                            onClick={() => setUserMenuOpen(false)}
                            className="mt-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-900/30 text-primary-300 border border-primary-800 hover:bg-primary-900/50 transition-colors"
                          >
                            <LockClosedIcon aria-hidden="true" className="w-3 h-3 mr-1.5" />
                            No active plan — Subscribe
                          </Link>
                        ) : hasActivePlan ? (
                          <div className="mt-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-900/30 text-green-400 border border-green-800">
                            <span className="w-2 h-2 bg-green-400 rounded-full mr-1.5"></span>
                            Active Subscription
                          </div>
                        ) : null}
                      </div>
                      
                      <div className="py-1">
                        <Link role="menuitem" to="/dashboard/profile" onClick={() => setUserMenuOpen(false)} className="block px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-dark-750 transition-colors">
                          <UserIcon aria-hidden="true" className="w-4 h-4 inline mr-2" />Profile Settings
                        </Link>
                        <Link role="menuitem" to="/dashboard/subscription" onClick={() => setUserMenuOpen(false)} className="block px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-dark-750 transition-colors">
                          <Cog6ToothIcon aria-hidden="true" className="w-4 h-4 inline mr-2" />Subscription
                        </Link>
                      </div>

                      <div className="border-t border-dark-700 pt-1">
                        <button
                          type="button"
                          role="menuitem"
                          onClick={handleLogout}
                          className="block w-full text-left px-4 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-red-950/30 transition-colors"
                        >
                          <ArrowRightOnRectangleIcon aria-hidden="true" className="w-4 h-4 inline mr-2" />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="text-gray-300 hover:text-white hover:bg-dark-800">
                    Sign In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 shadow-lg hover:shadow-primary-500/25 transition-all duration-300">
                    Get Started
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close main menu' : 'Open main menu'}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-menu"
              className="p-2 rounded-xl text-gray-400 hover:text-white hover:bg-dark-800/60 transition-all duration-200 border border-dark-700/50 hover:border-dark-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-dark-900"
            >
              {mobileMenuOpen ? (
                <XMarkIcon className="w-6 h-6" aria-hidden="true" />
              ) : (
                <Bars3Icon className="w-6 h-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div id="mobile-menu" className="lg:hidden border-t border-dark-700/50 bg-dark-900/98 backdrop-blur-md max-h-[calc(100vh-80px)] overflow-y-auto">
          <div className="px-4 py-6 space-y-3">
            {navItems.map((item) => (
              isLocked(item) ? (
                <button
                  key={item.name}
                  type="button"
                  onClick={handleLockedClick}
                  aria-label={`${item.name} — locked, subscribe to unlock`}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-base font-medium text-gray-500 hover:text-gray-300 hover:bg-dark-800/40 transition-all duration-200"
                >
                  <span>{item.name}</span>
                  <LockClosedIcon aria-hidden="true" className="w-4 h-4" />
                </button>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  aria-current={item.current ? 'page' : undefined}
                  className={`block px-4 py-3 rounded-xl text-base font-medium transition-all duration-200 ${
                    item.current
                      ? 'text-primary-400 bg-primary-500/10 border border-primary-500/20'
                      : 'text-gray-300 hover:text-white hover:bg-dark-800/60'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              )
            ))}
            
            {/* Mobile Auth Section */}
            <div className="pt-6 border-t border-dark-700/50">
              {isAuthenticated ? (
                <div className="space-y-4">
                  <Link
                    to="/dashboard/profile"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center space-x-3 px-4 py-3 bg-dark-800/40 rounded-xl border border-dark-700/50 hover:bg-dark-800/70 transition-colors"
                  >
                    <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-lg flex items-center justify-center text-white font-semibold">
                      {user?.full_name?.charAt(0) || 'U'}
                    </div>
                    <div>
                      <p className="text-white font-medium">{user?.full_name || 'User'}</p>
                      <p className="text-xs text-gray-400">Premium Account</p>
                    </div>
                  </Link>
                  {/* On private pages these already exist in navItems above */}
                  {!isPrivate && !isAdmin && (
                    <>
                      <Link
                        to="/dashboard/profile"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block px-4 py-3 rounded-xl text-base font-medium text-gray-300 hover:text-white hover:bg-dark-800/60 transition-all duration-200"
                      >
                        <UserIcon aria-hidden="true" className="w-4 h-4 inline mr-2" />Profile Settings
                      </Link>
                      <Link
                        to="/dashboard/subscription"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block px-4 py-3 rounded-xl text-base font-medium text-gray-300 hover:text-white hover:bg-dark-800/60 transition-all duration-200"
                      >
                        <Cog6ToothIcon aria-hidden="true" className="w-4 h-4 inline mr-2" />Subscription
                      </Link>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-red-600/10 text-red-400 rounded-xl border border-red-600/20 hover:bg-red-600/20 transition-colors"
                  >
                    <ArrowRightOnRectangleIcon className="w-5 h-5" aria-hidden="true" />
                    <span>Sign Out</span>
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <Link
                    to="/login"
                    className="block px-4 py-3 text-center rounded-xl text-gray-300 border border-dark-600 hover:bg-dark-800 hover:text-white transition-all"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    className="block px-4 py-3 text-center rounded-xl text-white bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 shadow-lg transition-all"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Get Started
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  )
}

export default Navigation
